import { useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import tinycolor from 'tinycolor2'
import { map, noop } from 'lodash'
import LinearScale from 'linear-scale'
import { Motion, spring } from 'react-motion'
import { rectsIntersect } from '@utils/trig.js'
import { calculateWeightedAverage, clamp } from '@utils/math.js'
import { markupAndSanitize, sanitize } from '@utils/sanitize.ts'
import { getReadableTextColor } from '@helpers/color_scheme_helpers.js'
import { ThemeContext } from '@base/ThemeContext.js'
import useAnimationFrame from '@hooks/useAnimationFrame.ts'
import ModalLayer from '@components/ModalLayer.tsx'
import FormattedTime from '@components/FormattedTime.tsx'
import BackgroundContainer from '../../containers/background_container.jsx'
import ModalCard from '../modal_card.jsx'
import MarkerPositionIndicator from '../marker_position_indicator.jsx'
import Marker from './marker.jsx'
import Timespan from './timespan.jsx'
import Context from '../../context.js'
import { startFreeScroll, cancelFreeScroll } from '../../actions.js'

const Timeline = props => {
  const { scrollerData } = props
  const colorScheme = useContext(ThemeContext)

  const { state, dispatch } = useContext(Context)
  const { freeScrolling } = state

  const prevPositionRef = useRef(null)
  const velocityXRef = useRef(0)
  const lastAnimationTimestampRef = useRef(null)
  const positionsRef = useRef([])
  const friction = 0.95

  // setup for useAnimationFrame hook
  const [shouldAnimate, setShouldAnimate] = useState(false)
  useEffect(() => {
    // update local state when freeScrolling changes so animation hook can turn on/off
    setShouldAnimate(freeScrolling)
  }, [freeScrolling])

  // keep the fresh scroller data in a ref so we can use it in the animation frame handler
  const scrollerDataRef = useRef(scrollerData)
  useEffect(() => {
    scrollerDataRef.current = scrollerData
  }, [scrollerData])

  // called by the useAnimationFrame hook
  const nextAnimationFrameHandler = () => {
    // velocity is measured in pixels per ms so we need to keep track of milliseconds since last tick run to get accurate values
    const lastTimestamp = lastAnimationTimestampRef.current
    const currentTimestamp = Date.now()
    if (lastTimestamp) {
      // calculate the time in ms since last run, but clamp so we don't get huge values if the tab is backgrounded
      // this is basically the time since the last animation tick
      let deltaTime = clamp(currentTimestamp - lastTimestamp, 0, 1000)

      // Apply friction to velocities
      // TODO: Also take into account the deltaTime here as we might run a lot, or a little
      velocityXRef.current *= friction

      const absoluteVelocity = Math.abs(velocityXRef.current)
      if (absoluteVelocity < 0.01) {
        // Stop when velocity is negligible
        //console.log("Neglibile movement, stopping")
        lastAnimationTimestampRef.current = null
        cancelAnimation()
      } else {
        const movementX = velocityXRef.current * deltaTime

        //console.log("Freescroll movement X:", movementX, "px since last tick run at", deltaTime, "ms")

        // use scroller data from ref so we can avoid stale state inclosure issues
        const scrollerData = scrollerDataRef.current
        const surfaceX = scrollerData.surface.x
        const targetSurfaceX = Math.round(surfaceX + movementX)
        const diffX = targetSurfaceX - surfaceX

        //console.log("Gonna freescroll surface by", diffX, "px")

        const minX = 0 + scrollerData.viewport.width - scrollerData.surface.width
        const maxX = 0
        const nextSurfaceX = clamp(scrollerData.surface.x + diffX, minX, maxX)

        // To calculate the new left and right, we pretend the surface is stationary, moving the viewport instead
        // Note: We also clamp the next surface x to the bounds it can scroll to
        const newViewportLeft = Math.abs(nextSurfaceX)
        const newViewportRight = newViewportLeft + scrollerData.viewport.width

        //now calculate the next left and right position by using the timeline width
        const scale = LinearScale([0, scrollerData.surface.width], [0, 1])
        const nextLeft = scale(newViewportLeft)
        const nextRight = scale(newViewportRight)

        props.onPanMove(nextLeft, nextRight)
        lastAnimationTimestampRef.current = currentTimestamp
      }
    } else {
      // had no timestamp yet, so set it and line up next animation tick run
      lastAnimationTimestampRef.current = currentTimestamp
    }
  }

  useAnimationFrame(nextAnimationFrameHandler, freeScrolling)

  const resetAnimationVariables = () => {
    prevPositionRef.current = null
    positionsRef.current = []
    lastAnimationTimestampRef.current = null
  }

  const handlePanStart = hammerEvent => {
    // Note: Don't prevent default here or vertical page scrolling won't work on touch devices
    cancelAnimation()
    resetAnimationVariables()
    const { timeStamp, center } = hammerEvent
    prevPositionRef.current = center
    trackPosition(center.x, center.y, timeStamp)
    props.onPanStart()
  }

  const handlePanMove = hammerEvent => {
    // Note: Don't prevent default here or vertical page scrolling won't work on touch devices
    const { center, timeStamp } = hammerEvent
    trackPosition(center.x, center.y, timeStamp)

    const prevCenter = prevPositionRef.current
    prevPositionRef.current = center
    // Note: Occasionally prevCenter is null here, so we need to check for that
    if (prevCenter) {
      // Note: Assigning to variables as one of the objects is sometimes null and we would like to know which one (in production)
      const prevCenterX = prevCenter.x
      const currentCenterX = center.x

      const movementX = (prevCenterX - currentCenterX) * -1
      const movementY = 0

      timelineDrag(movementX, movementY)
    }
  }

  const handlePanEnd = hammerEvent => {
    // now set the velocity to the average velocity over the last few frames
    calculateVelocity()
    resetAnimationVariables()

    props.onPanEnd()
    dispatch(startFreeScroll())
  }

  const trackPosition = (x, y, timeStamp) => {
    const existingPositions = positionsRef.current

    // Track up to 100 positions
    let updatedPositions = existingPositions.length >= 100 ? existingPositions.slice(1, 100) : existingPositions.slice()

    updatedPositions.push({ x, y, timeStamp })
    positionsRef.current = updatedPositions
  }

  const calculateVelocity = () => {
    // only look at the last positions
    const cutoff = Date.now() - 200
    const recentPositions = positionsRef.current.filter(p => p.timeStamp > cutoff)
    if (recentPositions.length < 2) {
      //console.log("Not enough recent positions to calculate any real velocity")
      velocityXRef.current = 0
    } else {
      let velocities = recentPositions.slice(1).reduce((acc, position, i) => {
        const prevPosition = recentPositions[i]
        const elapsedMS = position.timeStamp - prevPosition.timeStamp
        const velocity = (position.x - prevPosition.x) / elapsedMS
        acc.push(velocity)
        return acc
      }, [])
      // store the weighted average velocity in pixels per ms
      velocityXRef.current = calculateWeightedAverage(velocities, 4)
    }
    //console.log("Stored velocity X as", velocityXRef.current, "AKA", velocityXRef.current * 1000, "px/s")
  }

  const handleTimelineItemTap = (hammerEvent, { id, timelineItemType }) => {
    props.onTimelineItemDrillDown(id, timelineItemType)
  }

  const handleTimelineItemTapClose = (hammerEvent, { id, timelineItemType }) => {
    props.onTimelineItemDrillUp(id, timelineItemType)
  }

  const cancelAnimation = () => {
    dispatch(cancelFreeScroll())
  }

  const timelineDrag = (movementX, movementY) => {
    //console.group("<----- CALCULATE NEXT LEFT AND RIGHT VALUES ----->")
    const { scrollerData } = props

    //console.log(`Original left and right: ${scrollerData.left}, ${scrollerData.right}`)
    // we know left and right, as well as our timeline surface width
    // convert the new dragged left and right values in relation to the surface width so the action can pass the new left and right values in the reducer
    // console.log("Drag movement is ", movementX)

    const viewportWidth = scrollerData.viewport.width
    const surfaceX = scrollerData.surface.x
    const surfaceWidth = scrollerData.surface.width

    const minX = 0 + viewportWidth - surfaceWidth
    const maxX = 0
    // Note: We clamp the next surface x to the bounds it can scroll to
    const nextSurfaceX = clamp(surfaceX + movementX, minX, maxX)

    // now pretend the surface is stationary, moving the viewport so we can calculate the new left and right values
    const newViewportLeft = Math.abs(nextSurfaceX)
    const newViewportRight = newViewportLeft + viewportWidth

    // now calculate the next left and right position by using the timeline width
    const scale = LinearScale([0, surfaceWidth], [0, 1])
    const nextLeft = scale(newViewportLeft)
    const nextRight = scale(newViewportRight)

    //console.log(`Next left and right: ${nextLeft}, ${nextRight}`)

    //console.groupEnd()
    props.onPanMove(nextLeft, nextRight)
  }

  const isOnStage = itemDimensions => {
    const { timelineDisplayBounds } = props
    const viewportDimensions = timelineDisplayBounds.viewport
    const bounds = {
      x: viewportDimensions.x,
      y: viewportDimensions.y,
      width: viewportDimensions.width,
      height: viewportDimensions.height,
    }
    return rectsIntersect(bounds, itemDimensions)
  }

  const getBackgroundContent = () => {
    const { markers, timespans } = props
    return (
      <BackgroundContainer
        markers={markers}
        timespans={timespans}
        onPanStart={handlePanStart}
        onPanMove={handlePanMove}
        onPanEnd={handlePanEnd}
      />
    )
  }

  const getMarkerItemsContent = (offset = 0, yScale = null) => {
    const {
      markers,
      displayableTimelineItems,
      drilledDownTimelineItem,
      expandedItemBounds,
      stageWidth,
      stageHeight,
      timelineDisplayBounds,
      photos,
    } = props

    if (markers != null) {
      return map(markers, marker => {
        let targetState, zIndex, x, y, width, height, positionIndicator, modalLayer
        const markerId = marker.id
        if (
          drilledDownTimelineItem &&
          drilledDownTimelineItem.timelineItemType === 'marker' &&
          drilledDownTimelineItem.id === markerId
        ) {
          targetState = 'expanded'
          x = expandedItemBounds.x
          y = expandedItemBounds.y
          width = expandedItemBounds.width
          height = expandedItemBounds.height
          zIndex = 31
        } else {
          const item = displayableTimelineItems.markers[markerId.toString()]
          targetState = 'contracted'
          x = item.x + offset
          y = yScale === null ? item.y : yScale(item.y)
          //console.log("Placing marker at y:", y)
          width = item.width
          height = item.height
          zIndex = 20
        }

        if (targetState === 'expanded') {
          modalLayer = (
            <CSSTransition classNames="fade-in-200-out-200" timeout={200}>
              <ModalLayer
                key={`marker-${markerId}-modal-layer`}
                position={'absolute'}
                width={stageWidth}
                height={stageHeight}
                zIndex={30}
                onTap={handleTimelineItemTapClose}
                onTapArgs={{ id: markerId, timelineItemType: 'marker' }}
              />
            </CSSTransition>
          )
        }

        return (
          <Motion
            key={`motion-marker-${markerId}`}
            defaultStyle={{ x: x, y: y, width: width, height: height }}
            style={{ x: spring(x), y: spring(y), width: spring(width), height: spring(height) }}
          >
            {interpolated => {
              const visible = isOnStage({
                x: interpolated.x,
                y: interpolated.y,
                width: interpolated.width,
                height: interpolated.height,
              })
              //console.log("Attempting to render marker at", x, y, visible ? "is visIble" : 'is not visible')

              if (visible) {
                positionIndicator = null
                if (targetState === 'contracted') {
                  const lineBottomMargin = 8
                  const lineY = y + height
                  const axisMargin = 50
                  const indicatorData = {
                    x: interpolated.x + 1,
                    y: lineY,
                    height: timelineDisplayBounds.viewport.height + axisMargin - lineY - lineBottomMargin,
                  }
                  positionIndicator = (
                    <MarkerPositionIndicator
                      key={`marker-${markerId}-positionIndicator`}
                      x={indicatorData.x}
                      y={indicatorData.y}
                      height={indicatorData.height}
                      colorScheme={colorScheme}
                    />
                  )
                }
                let photoData = null
                if (marker.photo_ids.length > 0) {
                  photoData = photos[marker.photo_ids[0].toString()]
                }
                return (
                  <div>
                    <Marker
                      key={`marker-${markerId}`}
                      marker={marker}
                      photoData={photoData}
                      x={interpolated.x}
                      y={interpolated.y}
                      width={interpolated.width}
                      height={interpolated.height}
                      targetX={x}
                      targetY={y}
                      targetWidth={width}
                      targetHeight={height}
                      expandedWidth={expandedItemBounds.width}
                      expandedHeight={expandedItemBounds.height}
                      zIndex={zIndex}
                      targetState={targetState}
                      colorScheme={colorScheme}
                      onTap={handleTimelineItemTap}
                      onTapClose={handleTimelineItemTapClose}
                      onPanStart={handlePanStart}
                      onPanMove={handlePanMove}
                      onPanEnd={handlePanEnd}
                    />
                    {positionIndicator}
                    <TransitionGroup>{modalLayer}</TransitionGroup>
                  </div>
                )
              }
              return null
            }}
          </Motion>
        )
      })
    }
    return null
  }

  const getTimespanItemsContent = (offset = 0, yScale = null) => {
    const {
      timespans,
      displayableTimelineItems,
      drilledDownTimelineItem,
      stageWidth,
      stageHeight,
      timelineDisplayBounds,
      expandedItemBounds,
    } = props
    // card colors
    const cardHeaderColor = colorScheme.highlight
    const cardHeaderTextColor = getReadableTextColor(cardHeaderColor, colorScheme)
    const cardBodyColor = colorScheme.secondary

    if (timespans != null) {
      return map(timespans, timespan => {
        const timespanId = timespan.id
        const item = displayableTimelineItems.timespans[timespanId.toString()]
        const x = item.x + offset
        const y = yScale === null ? item.y : yScale(item.y)
        //console.log("Placing timespan at y:", y)
        const width = item.width
        const height = item.height
        const zIndex = 20
        const targetState =
          drilledDownTimelineItem &&
          drilledDownTimelineItem.timelineItemType === 'timespan' &&
          drilledDownTimelineItem.id === timespanId
            ? 'expanded'
            : 'contracted'

        let modalCard = null
        if (targetState === 'expanded') {
          const cardWidth = expandedItemBounds.width
          const cardHeight = expandedItemBounds.height
          const cardX = expandedItemBounds.x
          const cardY = expandedItemBounds.y

          const expandedContentStyle = { width: cardWidth }
          const description = timespan.description || 'No description.'

          // style for modal card
          const modalHeaderStyle = { backgroundColor: cardHeaderColor }
          const modalHeaderTextStyle = { color: cardHeaderTextColor }
          const tinyColor = tinycolor(cardHeaderTextColor)
          const gradientMiddleColor = tinyColor.setAlpha(0.75).toRgbString()
          const gradientEdgeColor = tinyColor.setAlpha(0.5).toRgbString()
          const headerHorizontalRuleStyle = {
            backgroundImage: `linear-gradient(to right, ${gradientEdgeColor}, ${gradientMiddleColor}, ${gradientEdgeColor})`,
          }

          modalCard = (
            <CSSTransition classNames="fade-in-out" timeout={600}>
              <div
                key={`timespan-${timespanId}-modal-card`}
                className="timeline-modal-card-holder"
                style={{ zIndex: 999 }}
              >
                <ModalCard
                  width={stageWidth}
                  height={stageHeight}
                  cardWidth={cardWidth}
                  cardHeight={cardHeight}
                  cardX={cardX}
                  cardY={cardY}
                  colorScheme={colorScheme}
                  onTapClose={handleTimelineItemTapClose}
                  onTapCloseArgs={{ id: timespanId, timelineItemType: 'timespan' }}
                >
                  <div className="expanded-content" style={expandedContentStyle}>
                    <div className="tooltip-title" style={modalHeaderStyle}>
                      <h1 style={modalHeaderTextStyle}>
                        <FormattedTime dateTime={timespan.start_date} color={cardHeaderTextColor} /> &ndash;{' '}
                        <FormattedTime dateTime={timespan.end_date} color={cardHeaderTextColor} />
                        <hr style={headerHorizontalRuleStyle}></hr>
                        <span dangerouslySetInnerHTML={{ __html: sanitize(timespan.title) }}></span>
                      </h1>
                    </div>
                    <p
                      className="tooltip-description"
                      dangerouslySetInnerHTML={{ __html: markupAndSanitize(description) }}
                    />
                  </div>
                </ModalCard>
              </div>
            </CSSTransition>
          )
        }

        return (
          <Motion
            key={`motion-timespan-${timespanId}`}
            defaultStyle={{ x: x, y: y, width: width, height: height }}
            style={{ x: spring(x), y: spring(y), width: spring(width), height: spring(height) }}
          >
            {interpolated => {
              const visible = isOnStage({
                x: interpolated.x,
                y: interpolated.y,
                width: interpolated.width,
                height: interpolated.height,
              })
              if (visible) {
                return (
                  <div>
                    <Timespan
                      key={`timespan-${timespanId}`}
                      timespan={timespan}
                      title={timespan.title}
                      x={interpolated.x}
                      y={interpolated.y}
                      width={interpolated.width}
                      height={interpolated.height}
                      zIndex={zIndex}
                      onTap={handleTimelineItemTap}
                      onTapClose={handleTimelineItemTapClose}
                      onPanStart={handlePanStart}
                      onPanMove={handlePanMove}
                      onPanEnd={handlePanEnd}
                      colorScheme={colorScheme}
                    />
                    <TransitionGroup key={`timespan-${timespanId}-transition-group`}>{modalCard}</TransitionGroup>
                  </div>
                )
              }
              return null
            }}
          </Motion>
        )
      })
    }
    return null
  }

  const { timelineDisplayBounds, displayableTimelineItems } = props
  const offset = timelineDisplayBounds.surface.x
  const surfaceHeight = timelineDisplayBounds.surface.height
  const topY = displayableTimelineItems.topY
  const yScale = topY < 0 ? LinearScale([topY, surfaceHeight], [5, surfaceHeight - 5]) : null
  const background = getBackgroundContent()
  const markerItems = getMarkerItemsContent(offset, yScale)
  const timespanItems = getTimespanItemsContent(offset, yScale)
  return (
    <div className="html-timeline">
      {background}
      {markerItems}
      {timespanItems}
    </div>
  )
}
Timeline.propTypes = {
  timeline: PropTypes.object.isRequired,
  markers: PropTypes.array.isRequired,
  timespans: PropTypes.array.isRequired,
  timelineDisplayBounds: PropTypes.shape({
    surface: PropTypes.shape({
      x: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }).isRequired,
    viewport: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  expandedItemBounds: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  displayableTimelineItems: PropTypes.shape({
    markers: PropTypes.object.isRequired,
    timespans: PropTypes.object.isRequired,
  }).isRequired,
  photos: PropTypes.object.isRequired,
  stageWidth: PropTypes.number.isRequired,
  stageHeight: PropTypes.number.isRequired,
  onPanStart: PropTypes.func.isRequired,
  onPanMove: PropTypes.func.isRequired,
  onPanEnd: PropTypes.func.isRequired,
  onTimelineItemDrillDown: PropTypes.func.isRequired,
  onTimelineItemDrillUp: PropTypes.func.isRequired,
}
Timeline.defaultProps = {
  onPanStart: noop,
  onPanMove: noop,
  onPanEnd: noop,
  onTimelineItemDrillDown: noop,
  onTimelineItemDrillUp: noop,
}
export default Timeline
